// src/components/common/taskbox/TasksWidget.js
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MoreVertical, Edit2, Trash2, Copy, Calendar, CheckSquare } from 'lucide-react';
import taskService from '../../../services/task.service';
import EditTaskModal from './EditTaskModal';

const PRIORITY_CONFIG = {
    immediate: { color: 'red', label: 'Immediate' },
    urgent: { color: 'orange', label: 'Urgent' },
    next_in_line: { color: 'yellow', label: 'Next in Line' },
    scheduled: { color: 'green', label: 'Scheduled' },
    deferred: { color: 'gray', label: 'Deferred' }
};

const TasksWidget = ({ tasks = [], activeView, onTaskCompleted, onRefresh, theme }) => {
    const [expandedTask, setExpandedTask] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    const calculateCompletionPercentage = () => {
        if (tasks.length === 0) return 0;
        const completed = tasks.filter(t => t.is_completed).length;
        return Math.round((completed / tasks.length) * 100);
    };

    const handleToggleSelect = (taskId) => {
        const newSelected = new Set(selectedTasks);
        if (newSelected.has(taskId)) {
            newSelected.delete(taskId);
        } else {
            newSelected.add(taskId);
        }
        setSelectedTasks(newSelected);
    };

    const handleMarkAsComplete = async () => {
        if (selectedTasks.size === 0) {
            alert('Please select tasks to mark as complete');
            return;
        }

        try {
            // Complete all selected tasks
            await Promise.all(
                Array.from(selectedTasks).map(taskId => taskService.completeTask(taskId))
            );

            // Clear selection
            setSelectedTasks(new Set());

            // Refresh the task list
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error marking tasks as complete:', error);
            alert('Failed to mark tasks as complete. Please try again.');
        }
    };

    const handleUncompleteTask = async (taskId) => {
        try {
            await taskService.uncompleteTask(taskId);
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error uncompleting task:', error);
        }
    };

    const handleDelete = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) {
            return;
        }

        try {
            await taskService.deleteTask(taskId);
            setOpenMenu(null);
            if (onRefresh) {
                onRefresh();
            }
            alert('Task deleted successfully!');
        } catch (error) {
            console.error('Error deleting task:', error);
            alert('Failed to delete task. Please try again.');
        }
    };

    const handleDuplicate = async (task) => {
        try {
            const duplicateData = {
                title: `${task.title} (Copy)`,
                description: task.description,
                task_type: task.task_type,
                priority: task.priority,
                deadline: task.deadline,
                category: task.category
            };

            await taskService.createTask(duplicateData);
            setOpenMenu(null);
            if (onRefresh) {
                onRefresh();
            }
            alert('Task duplicated successfully!');
        } catch (error) {
            console.error('Error duplicating task:', error);
            alert('Failed to duplicate task. Please try again.');
        }
    };

    const handleEdit = (task) => {
        setOpenMenu(null);
        setEditingTask(task);
    };

    const handleSaveEdit = async (taskId, taskData) => {
        try {
            // Extract checklist from taskData since it's handled separately
            const { checklist, ...taskFields } = taskData;

            // Update the task itself
            await taskService.updateTask(taskId, taskFields);

            // Handle checklist updates
            if (checklist) {
                const originalChecklist = editingTask.checklist || [];

                // Find new items (items with isNew flag)
                const newItems = checklist.filter(item => item.isNew);
                for (const item of newItems) {
                    await taskService.addChecklistItem(taskId, {
                        title: item.title,
                        description: item.description || null
                    });
                }

                // Find items to update (changed is_completed status)
                for (const item of checklist) {
                    if (!item.isNew && item.id) {
                        const original = originalChecklist.find(o => o.id === item.id);
                        if (original && original.is_completed !== item.is_completed) {
                            await taskService.updateChecklistItem(taskId, item.id, {
                                is_completed: item.is_completed
                            });
                        }
                    }
                }

                // Find items to delete (removed from checklist)
                const currentIds = new Set(checklist.filter(i => i.id).map(i => i.id));
                for (const original of originalChecklist) {
                    if (original.id && !currentIds.has(original.id)) {
                        await taskService.deleteChecklistItem(taskId, original.id);
                    }
                }
            }

            setEditingTask(null);
            if (onRefresh) {
                onRefresh();
            }
            alert('Task updated successfully!');
        } catch (error) {
            console.error('Error updating task:', error);
            alert('Failed to update task. Please try again.');
        }
    };

    const handleCloseEdit = () => {
        setEditingTask(null);
    };

    const getPriorityClass = (priority) => {
        const colors = {
            immediate: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
            urgent: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
            next_in_line: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
            scheduled: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
            deferred: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
        };
        return colors[priority] || colors.scheduled;
    };

    const completionPercentage = calculateCompletionPercentage();
    const pendingTasks = tasks.filter(t => !t.is_completed);
    const completedTasks = tasks.filter(t => t.is_completed);

    // Backend handles sorting by: priority → deadline → created_at
    // Just use tasks as-is (already sorted from backend)
    const sortedTasks = tasks;

    return (
        <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    {activeView.charAt(0).toUpperCase() + activeView.slice(1)} Tasks
                </h3>
                <div className="flex items-center gap-2">
                    <span className={`text-sm px-3 py-1 rounded-full ${
                        theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                    }`}>
                        {pendingTasks.length} pending
                    </span>
                    {selectedTasks.size > 0 && (
                        <button
                            onClick={handleMarkAsComplete}
                            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-full transition-colors flex items-center gap-1"
                        >
                            ✓ Mark {selectedTasks.size} as Complete
                        </button>
                    )}
                </div>
            </div>

            {/* Completion Progress */}
            <div className={`p-4 rounded-lg mb-4 ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
            }`}>
                <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                        Overall Completion
                    </span>
                    <span className={`text-lg font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {completionPercentage}%
                    </span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${
                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                }`}>
                    <div
                        className="h-full bg-blue-600 transition-all duration-500"
                        style={{ width: `${completionPercentage}%` }}
                    />
                </div>
                <div className={`flex justify-between mt-2 text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                    <span>{completedTasks.length} completed</span>
                    <span>{pendingTasks.length} remaining</span>
                </div>
            </div>

            {/* Tasks List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
                {tasks.length === 0 ? (
                    <div className="text-center py-8">
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            No tasks yet. Create your first task above!
                        </p>
                    </div>
                ) : (
                    <>
                        {sortedTasks.map(task => (
                            <div
                                key={task.id}
                                className={`p-4 rounded-lg border transition-all ${
                                    task.is_completed
                                        ? theme === 'dark'
                                            ? 'bg-gray-700/30 border-gray-600 opacity-70'
                                            : 'bg-gray-100 border-gray-300 opacity-70'
                                        : theme === 'dark'
                                            ? 'bg-gray-700/50 border-gray-600 hover:border-blue-500'
                                            : 'bg-gray-50 border-gray-200 hover:border-blue-400'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    {!task.is_completed ? (
                                        <input
                                            type="checkbox"
                                            checked={selectedTasks.has(task.id)}
                                            onChange={() => handleToggleSelect(task.id)}
                                            className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    ) : (
                                        <button
                                            onClick={() => handleUncompleteTask(task.id)}
                                            className="mt-1 w-4 h-4 flex items-center justify-center text-green-600 hover:text-green-700"
                                            title="Click to uncomplete"
                                        >
                                            ✓
                                        </button>
                                    )}
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-2 flex-1">
                                                <h4 className={`font-medium ${
                                                    task.is_completed ? 'line-through' : ''
                                                } ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {task.title}
                                                </h4>
                                                {/* Expand/Collapse Button */}
                                                {(task.checklist?.length > 0 || task.description || task.category) && (
                                                    <button
                                                        onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                                                        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                        }`}
                                                        title={expandedTask === task.id ? 'Collapse details' : 'Expand details'}
                                                    >
                                                        {expandedTask === task.id ? (
                                                            <ChevronUp className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronDown className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {/* Deadline */}
                                                {task.deadline && (
                                                    <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                                                        theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{new Date(task.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' })}</span>
                                                    </div>
                                                )}
                                                {/* Priority */}
                                                <span className={`text-xs px-2 py-1 rounded-full ${getPriorityClass(task.priority)}`}>
                                                    {PRIORITY_CONFIG[task.priority]?.label}
                                                </span>
                                                {/* Actions Menu */}
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            if (openMenu === task.id) {
                                                                setOpenMenu(null);
                                                            } else {
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                setMenuPosition({
                                                                    top: rect.top - 140, // Position menu above button (approximate menu height)
                                                                    right: window.innerWidth - rect.right
                                                                });
                                                                setOpenMenu(task.id);
                                                            }
                                                        }}
                                                        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
                                                    >
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>

                                                    {openMenu === task.id && (
                                                        <>
                                                            {/* Backdrop to close menu when clicking outside */}
                                                            <div
                                                                className="fixed inset-0 z-40"
                                                                onClick={() => setOpenMenu(null)}
                                                            />
                                                            {/* Menu */}
                                                            <div
                                                                className={`fixed w-48 rounded-lg shadow-xl border z-50 ${
                                                                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                                                }`}
                                                                style={{
                                                                    top: `${menuPosition.top}px`,
                                                                    right: `${menuPosition.right}px`
                                                                }}
                                                            >
                                                                <button
                                                                    onClick={() => handleEdit(task)}
                                                                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-t-lg ${
                                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                                    }`}
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDuplicate(task)}
                                                                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                                    }`}
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                    Duplicate
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(task.id)}
                                                                    className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-b-lg"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded Details */}
                                        {expandedTask === task.id && (
                                            <div className={`mt-3 pt-3 border-t space-y-3 ${
                                                theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                                            }`}>
                                                {/* Checklist */}
                                                {task.checklist?.length > 0 && (
                                                    <div>
                                                        <div className={`flex items-center gap-1 text-sm font-medium mb-2 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            <CheckSquare className="w-4 h-4" />
                                                            <span>Checklist ({task.checklist.filter(i => i.is_completed).length}/{task.checklist.length})</span>
                                                        </div>
                                                        <div className="space-y-1.5 ml-5">
                                                            {task.checklist.map((item, idx) => (
                                                                <div key={idx} className="flex items-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={item.is_completed}
                                                                        disabled
                                                                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
                                                                    />
                                                                    <span className={`text-sm ${
                                                                        item.is_completed
                                                                            ? `line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`
                                                                            : theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                                    }`}>
                                                                        {item.title}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Description */}
                                                {task.description && (
                                                    <div>
                                                        <div className={`text-sm font-medium mb-1 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Description
                                                        </div>
                                                        <p className={`text-sm ml-0 ${
                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                        }`}>
                                                            {task.description}
                                                        </p>
                                                    </div>
                                                )}
                                                {/* Category */}
                                                {task.category && (
                                                    <div>
                                                        <div className={`text-sm font-medium mb-1 ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>
                                                            Category
                                                        </div>
                                                        <span className={`text-sm px-2 py-1 rounded ${
                                                            theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                                                        }`}>
                                                            {task.category.charAt(0).toUpperCase() + task.category.slice(1)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {/* Edit Task Modal */}
            {editingTask && (
                <EditTaskModal
                    task={editingTask}
                    onClose={handleCloseEdit}
                    onSave={handleSaveEdit}
                    theme={theme}
                />
            )}
        </div>
    );
};

export default TasksWidget;
